import {
  BitkitAlert,
  BitkitAvatar,
  BitkitBadge,
  BitkitLabel,
  BitkitLink,
  BitkitTooltip,
  rem,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { Text } from '@chakra-ui/react/text';
import { ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import defaultIcon from '@/assets/step-icon-default.svg';
import Clock from '@/components/Clock/Clock';
import Dot from '@/components/Dot/Dot';
import LogViewer from '@/components/LogViewer/LogViewer';
import { PAGE_HEADER_STICKY_HEIGHT } from '@/components/page/PageHeader/StickyPageHeader';
import { HEADER_HEIGHT } from '@/constants';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import { useStepLogChunks } from '@/pages/BuildDetailsPage/components/StructuredBuildLog/hooks/useStepLogChunks';
import linkifyMarkdown from '@/pages/BuildDetailsPage/components/StructuredBuildLog/utils/linkifyMarkdown';
import { formatDateTime } from '@/utils/commonFormats';
import { workflowEditorPath } from '@/utils/monolithApiRouteService';

import { StepSummary, StepSummaryStatus } from '../../types/BuildLogSummary';
import { connectorLineStyle } from './StepList';
import StepLogCard from './StepLogCard';
import StructuredBuildLogContext from './StructuredBuildLogContext';
import {
  createDocumentationUrl,
  formatErrorMessage,
  getStepStatusDecorator,
  isStepOpenByDefault,
  wrapWithAnsiColor,
} from './utils/log.utils';

type StepTitleLineProps = Pick<
  StepSummary,
  'title' | 'stepId' | 'status' | 'startedAt' | 'finishedAt' | 'deprecationNotice'
>;

const StepTitleLine = ({ title, stepId, status, startedAt, finishedAt, deprecationNotice }: StepTitleLineProps) => {
  const { stepsData } = useContext(StructuredBuildLogContext) || {};

  let iconURL;
  try {
    iconURL = stepsData?.stepIconURLs[stepId][stepsData?.latestStepVersions[stepId]] || defaultIcon;
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (error) {
    iconURL = defaultIcon;
  }

  const statusProps = getStepStatusDecorator(status);
  const StatusIcon = statusProps.icon;
  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="space-between"
      textAlign="initial"
      flexGrow="1"
      marginInlineEnd="16"
    >
      <Box alignItems="center" display="flex" gap="12">
        <BitkitTooltip text={statusProps.text}>
          <StatusIcon size="24" color={statusProps.textColor} aria-label={statusProps.text} />
        </BitkitTooltip>
        <BitkitTooltip text={stepId}>
          <BitkitAvatar variant="image" src={iconURL} name={stepId} />
        </BitkitTooltip>
        {title || stepId}
        {deprecationNotice && <BitkitBadge colorVariant="yellow">Deprecated</BitkitBadge>}
      </Box>
      {/*
      NOTE: We only show duration when both ends of the duration is present.

      BLS rate-limiting can drop event messages, leaving steps, or even the whole workflow stuck
      - When the step finished event is dropped, the step will stuck in Started status (without finishedAt)
      - When both the step started and step finished event is dropped, the step will stuck in NotStarted status (without startedAt and finishedAt)
    */}
      {startedAt && finishedAt && <Clock startDate={new Date(startedAt)} endDate={new Date(finishedAt)} />}
    </Box>
  );
};

type StepLogHeaderItemProps = {
  data: ReactNode;
  label: string;
};

const StepLogHeaderItem = ({ data, label }: StepLogHeaderItemProps) => (
  <Box flexGrow="1" flexShrink="1">
    <BitkitLabel as="dt">{label}</BitkitLabel>
    <Box as="dd" display="flex" alignItems="center" color="text/body" textStyle="comp/data/sm" wordBreak="break-all">
      {data}
    </Box>
  </Box>
);

const ResourceLink = ({ children, href }: { children?: ReactNode; href: HTMLAnchorElement['href'] }) => (
  <BitkitLink colorVariant="purple" href={href} isExternal whiteSpace="nowrap">
    {children}
  </BitkitLink>
);

type StepLogHeaderProps = {
  appSlug: string;
  triggeredWorkflow: string;
  canChangeSettings: boolean;
  step: StepSummary;
};

const StepLogHeader = ({ appSlug, canChangeSettings, triggeredWorkflow, step }: StepLogHeaderProps) => {
  const { sourceCodeUrl, startedAt, stepId, supportUrl, status, version, latestVersion, releaseNotes } = step;
  const documentationUrl = createDocumentationUrl(stepId);
  const hasStepResource = documentationUrl || supportUrl || sourceCodeUrl;
  const workflowEditorUrl = workflowEditorPath(appSlug, triggeredWorkflow);
  const updateAvailable = latestVersion && latestVersion !== version;
  const linksAvailable = updateAvailable && (!!releaseNotes || canChangeSettings);

  return (
    <Box as="dl" display="flex" flexDirection={['column', 'row']} gap={['16', '32']} justifyContent="space-between">
      <StepLogHeaderItem data={getStepStatusDecorator(status).text} label="Status" />
      <StepLogHeaderItem data={startedAt ? formatDateTime(startedAt) : startedAt} label="Started at" />
      <StepLogHeaderItem data={stepId} label="Step ID" />
      <StepLogHeaderItem
        data={
          <Box>
            <Box alignItems="center" display="flex">
              <Text whiteSpace="nowrap">{version}</Text>
              {updateAvailable && (
                <>
                  <Dot size="4" backgroundColor="text/body" marginInline={rem(10)} />
                  <Text color="status/warning/text" whiteSpace="nowrap">
                    Latest: {latestVersion}
                  </Text>
                </>
              )}
            </Box>
            {linksAvailable && (
              <Box alignItems="center" display="flex" marginBlockStart="4">
                {releaseNotes && <ResourceLink href={releaseNotes}>Release notes</ResourceLink>}
                {releaseNotes && canChangeSettings && <Dot size="4" backgroundColor="text/link" marginInline="6" />}
                {canChangeSettings && <ResourceLink href={workflowEditorUrl}>Update</ResourceLink>}
              </Box>
            )}
          </Box>
        }
        label="Version"
      />
      {hasStepResource && (
        <StepLogHeaderItem
          data={
            <Box alignItems="center" display="flex" gap="8" flexWrap="wrap">
              {documentationUrl && <ResourceLink href={documentationUrl}>Documentation</ResourceLink>}
              <Dot size="4" backgroundColor="text/secondary" />
              {supportUrl && <ResourceLink href={supportUrl}>Issue tracker</ResourceLink>}
              <Dot size="4" backgroundColor="text/secondary" />
              {sourceCodeUrl && <ResourceLink href={sourceCodeUrl}>Source code</ResourceLink>}
            </Box>
          }
          label="Step resources"
        />
      )}
    </Box>
  );
};

export const StepNotifications = ({
  retry,
  status,
  statusReason,
  deprecationNotice,
}: {
  retry: VoidFunction;
  status?: 'error' | 'deleted';
  statusReason?: string;
  deprecationNotice?: string;
}) => {
  return (
    <>
      {deprecationNotice && (
        <BitkitAlert
          variant="warning"
          titleText="This Step is deprecated."
          messageText={
            <pre style={{ font: 'inherit', whiteSpace: 'pre-wrap' }}>{linkifyMarkdown(deprecationNotice)}</pre>
          }
        />
      )}
      {statusReason && <BitkitAlert variant="info" messageText={statusReason} />}
      {status === 'deleted' && <BitkitAlert variant="info" messageText="The logs for this Step has been deleted." />}
      {status === 'error' && (
        <BitkitAlert
          variant="critical"
          action={{ label: 'Retry', onClick: retry }}
          messageText="Failed to load logs."
        />
      )}
    </>
  );
};

type StepListItemProps = {
  appSlug: string;
  buildSlug: string;
  triggeredWorkflow: string;
  canChangeSettings: boolean;
  viewedAsPublicPage: boolean;
  trackingId?: string;
  step: StepSummary;
};

const StepListItem = ({
  step,
  appSlug,
  buildSlug,
  triggeredWorkflow,
  canChangeSettings,
  viewedAsPublicPage,
  trackingId,
}: StepListItemProps) => {
  const { uuid, status, errors } = step;
  const shouldFetchLog = status !== StepSummaryStatus.Skipped && status !== StepSummaryStatus.SkippedWithRunIf;

  const { stepToOpen } = useContext(StructuredBuildLogContext) || {};

  const cardRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(uuid === stepToOpen || isStepOpenByDefault(status));
  const { chunks, isLoading, isSuccess, isError, isDeleted, refetch } = useStepLogChunks({
    uuid,
    appSlug,
    buildSlug,
    viewedAsPublicPage,
    enabled: isExpanded && shouldFetchLog,
  });
  const logText = useMemo(() => chunks.map(wrapWithAnsiColor).join(''), [chunks]);

  const onChange = (isOpened: boolean) => {
    segmentTrack('Expand Step Log Button Clicked', {
      app_slug: appSlug,
      build_log_type: 'structured',
      build_slug: buildSlug,
      is_step_log_open: isOpened,
      page_load_id: trackingId,
      step_id: step.stepId,
      step_status: step.status || 'unknown',
      step_title: step.title || 'unknown',
      step_version: step.version || 'unknown',
    });
    setIsExpanded(isOpened);
  };

  const onScrollNavigate = useCallback(
    (direction: 'up' | 'down') => {
      segmentTrack(`Expand Step Log Scroll Navigation Clicked`, {
        app_slug: appSlug,
        build_slug: buildSlug,
        page_load_id: trackingId,
        scroll_direction: direction,
      });
    },
    [appSlug, buildSlug, trackingId],
  );

  return (
    <StepLogCard
      ref={cardRef}
      as="li"
      id={uuid}
      className="step-list-item"
      css={connectorLineStyle}
      expanded={isExpanded}
      header={<StepTitleLine {...step} />}
      onExpandedChange={onChange}
      scrollMarginTop={`${HEADER_HEIGHT + PAGE_HEADER_STICKY_HEIGHT}px`}
    >
      <>
        <Box display="flex" flexDirection="column" padding="16" gap="16">
          <StepLogHeader
            appSlug={appSlug}
            canChangeSettings={canChangeSettings}
            triggeredWorkflow={triggeredWorkflow}
            step={step}
          />
          <StepNotifications
            retry={refetch}
            status={isError && isDeleted ? 'deleted' : isError ? 'error' : undefined}
            statusReason={step.statusReason}
            deprecationNotice={step.deprecationNotice}
          />
        </Box>
        {isLoading && <Skeleton height={rem(256)} />}
        {isSuccess && (
          <>
            {errors?.map((error) => (
              <BitkitAlert
                key={error.message}
                variant="critical"
                width="calc(100% - 2rem)"
                marginInline="16"
                marginBlockEnd="16"
                messageText={formatErrorMessage(error)}
              />
            ))}
            {chunks.length > 0 ? (
              <LogViewer content={logText} scrollTargetRef={cardRef} onScrollNavigate={onScrollNavigate} />
            ) : (
              <BitkitAlert
                variant="info"
                width="calc(100% - 2rem)"
                marginInline="16"
                marginBlockEnd="16"
                messageText="This Step did not generate any logs."
              />
            )}
          </>
        )}
      </>
    </StepLogCard>
  );
};

export default StepListItem;
